import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import * as pdfjsLib from 'pdfjs-dist';
import { DialogProtectedPdf } from '@/components/shared/file-uploader/dialog-protected-pdf';

// Desabilitar worker para evitar erros de fetch dinâmico
pdfjsLib.GlobalWorkerOptions.workerSrc = '';

const MAX_FILE_SIZE_MB = 2;

const formatFileSize = (size: number) => {
  const sizeInKb = size / 1024;
  const sizeInMb = size / 1024 ** 2;

  if (sizeInMb < 1) {
    return `${Math.floor(sizeInKb)} Kb`;
  }
  return `${Math.floor(sizeInMb)} Mb`;
};

interface FileItemUploadedProps {
  fileName: string;
  fileSize: number;
  onRemove: () => void;
  isFileTooLarge: boolean;
}

const FileItemUploaded = ({
  fileName,
  fileSize,
  onRemove,
  isFileTooLarge,
}: FileItemUploadedProps) => {
  return (
    <Stack mb={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={({ palette, shape, spacing }) => ({
          backgroundColor: palette.neutral[100],
          borderRadius: `${shape.borderRadius}px`,
          padding: spacing(1, 2),
        })}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <AttachFileIcon fontSize="small" color="disabled" />
          <Box>
            <Typography>{fileName}</Typography>
            <Typography>{formatFileSize(fileSize)}</Typography>
          </Box>
        </Stack>
        <IconButton sx={{ color: 'primary.dark' }} onClick={onRemove}>
          <DeleteOutlineIcon />
        </IconButton>
      </Stack>
      {isFileTooLarge && (
        <Typography color="error">
          (Arquivo excede 2MB e não pode ser anexado)
        </Typography>
      )}
    </Stack>
  );
};

export interface FileUploaderProps {
  onChange?: (files: File[]) => void;
  value?: File[];
  error?: boolean;
  helperText?: string;
  id?: string;
  labelId?: string;
}

export const FileUploader = forwardRef<HTMLInputElement, FileUploaderProps>(
  ({ onChange, value = [], error, helperText, id, labelId }, ref) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [protectedFiles, setProtectedFiles] = useState<string[]>([]);
    const [showProtectedDialog, setShowProtectedDialog] = useState(false);

    useImperativeHandle(ref, () => fileInputRef.current!);

    const checkPdfProtection = async (file: File): Promise<boolean> => {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const typedArray = new Uint8Array(arrayBuffer);
        const loadingTask = pdfjsLib.getDocument({ data: typedArray });
        await loadingTask.promise;
        return false; // Not protected
      } catch (err) {
        const pdfError = err as { name?: string };
        if (pdfError?.name === 'PasswordException') {
          return true; // Protected by password
        }
        // Other errors (worker failed, etc) - assume file is OK
        return false;
      }
    };

    const handleFileChange = async (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const selectedFiles = Array.from(event.target.files || []);
      const validFiles: File[] = [];
      const protectedPdfFiles: string[] = [];

      for (const file of selectedFiles) {
        if (file.type === 'application/pdf') {
          const isProtected = await checkPdfProtection(file);
          if (isProtected) {
            protectedPdfFiles.push(file.name);
          } else {
            validFiles.push(file);
          }
        } else {
          validFiles.push(file);
        }
      }

      if (protectedPdfFiles.length > 0) {
        setProtectedFiles(protectedPdfFiles);
        setShowProtectedDialog(true);
      }

      if (validFiles.length > 0) {
        onChange?.([...value, ...validFiles]);
      }

      // Reset input to allow selecting the same file again
      if (event.target) {
        event.target.value = '';
      }
    };

    const handleRemoveFile = (index: number) => {
      const updatedFiles = value.filter((_, i) => i !== index);
      onChange?.(updatedFiles);
    };

    const handleButtonClick = () => {
      fileInputRef.current?.click();
    };

    return (
      <>
        {value.length > 0 &&
          value.map((file, index) => {
            const fileSizeInMB = file.size / 1024 ** 2;
            const isFileTooLarge = fileSizeInMB > MAX_FILE_SIZE_MB;
            const fileName =
              file instanceof File
                ? file.name
                : typeof file === 'string'
                  ? file
                  : '';

            return (
              <FileItemUploaded
                key={`${fileName}-${index}`}
                fileSize={file.size}
                fileName={fileName}
                onRemove={() => handleRemoveFile(index)}
                isFileTooLarge={isFileTooLarge}
              />
            );
          })}
        <input
          accept="image/*,.pdf"
          style={{ display: 'none' }}
          id={id}
          multiple
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          aria-hidden="true"
          tabIndex={-1}
        />
        <Button
          startIcon={<AttachFileIcon />}
          variant="outlined"
          component="span"
          color={error ? 'error' : 'primary'}
          onClick={handleButtonClick}
          style={{ width: 'fit-content' }}
          aria-labelledby={labelId}
        >
          Anexar documento
        </Button>
        {error && helperText && (
          <FormHelperText error>{helperText}</FormHelperText>
        )}
        {showProtectedDialog && (
          <DialogProtectedPdf
            onClose={() => setShowProtectedDialog(false)}
            filesName={protectedFiles}
          />
        )}
      </>
    );
  }
);
